import React, { useState } from 'react';
import { Plus, Gift, Wand2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/money';
import { getProductById } from '@/lib/data';
import { PlaceholderImage } from './PlaceholderImage';
import { CustomizeHamperDialog } from './CustomizeHamperDialog';
import { TID } from '@/constants/testIds';

// Rectangular soap chip used inside bundle cards + hamper figure
const SoapTile = ({ item, size = 'sm' }) => {
  const base = size === 'lg' ? 'aspect-[16/10]' : 'aspect-[16/9]';
  return (
    <div className={`relative ${base} rounded-xl overflow-hidden border border-[var(--cw-border)] bg-white`}>
      {item.placeholder ? (
        <PlaceholderImage
          label={item.name}
          bg={item.placeholderColor}
          ink={item.placeholderInk}
          subLabel=""
        />
      ) : (
        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
      )}
    </div>
  );
};

// Small rectangular chip used inside "Inside the box" list rows
const Swatch = ({ item }) => (
  <span className="w-14 h-9 rounded-lg flex-shrink-0 overflow-hidden border border-[var(--cw-border)] bg-white">
    {item.placeholder ? (
      <PlaceholderImage
        label={item.name}
        bg={item.placeholderColor}
        ink={item.placeholderInk}
        subLabel=""
      />
    ) : (
      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
    )}
  </span>
);

export const BundleCard = ({ product, accent = 'yellow' }) => {
  const { addItem } = useCart();
  const [customOpen, setCustomOpen] = useState(false);
  const accentBg = accent === 'yellow' ? 'var(--cw-yellow)' : 'var(--cw-blue)';
  const items = (product.contains || []).map(getProductById).filter(Boolean);
  const isCustomizable = !!product.customizable;

  return (
    <article
      className="cw-card p-6 flex flex-col gap-5"
      data-testid={TID.productCard(product.id)}
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5 items-stretch">
        {/* Main figure */}
        <div className="md:col-span-3">
          {isCustomizable ? (
            <div className="relative rounded-2xl bg-[var(--cw-yellow)]/40 p-4 h-full">
              <div className="grid grid-cols-2 gap-3 h-full">
                {items.map((it) => (
                  <SoapTile key={it.id} item={it} size="lg" />
                ))}
              </div>
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white/85 backdrop-blur-sm rounded-full px-3 py-1 text-[var(--cw-text)] border border-white/70">
                <Wand2 className="w-3 h-3" /> Any {product.chooseCount} soaps
              </span>
              {product.badge && (
                <span className="cw-badge absolute top-3 right-3 shadow-sm">{product.badge}</span>
              )}
            </div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden bg-[#f4f6f8] aspect-[4/3] md:aspect-auto md:h-full">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {product.badge && (
                <span className="cw-badge absolute top-3 left-3 shadow-sm">{product.badge}</span>
              )}
              <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white/85 backdrop-blur-sm rounded-full px-3 py-1 text-[var(--cw-text)] border border-white/70">
                <Gift className="w-3 h-3" /> Contains {items.length} bars
              </span>
            </div>
          )}
        </div>

        {/* Contents list */}
        <div className="md:col-span-2 flex flex-col gap-2">
          <div className="cw-label">Inside the box</div>
          <ul className="flex flex-col gap-2 mt-1">
            {items.map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-3 bg-[var(--cw-bg)] rounded-xl px-3 py-2 border border-[var(--cw-border)]"
              >
                <Swatch item={it} />
                <span className="text-sm font-medium leading-tight">{it.name}</span>
              </li>
            ))}
          </ul>
          {isCustomizable && (
            <button
              type="button"
              onClick={() => setCustomOpen(true)}
              data-testid="hamper-customise-open"
              className="mt-2 self-start text-sm inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-[var(--cw-border)] bg-white hover:bg-[var(--cw-bg)] transition"
            >
              <Wand2 className="w-3.5 h-3.5" /> Customise your 4
            </button>
          )}
        </div>
      </div>

      <div className="flex items-end justify-between pt-1">
        <div>
          <h3 className="font-heading text-2xl font-medium leading-tight">{product.name}</h3>
          <p className="text-sm text-[var(--cw-muted)] mt-1">{product.tagline}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-heading text-2xl font-medium">{formatINR(product.price)}</div>
          <button
            type="button"
            onClick={() =>
              isCustomizable
                ? setCustomOpen(true)
                : addItem(product)
            }
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{ backgroundColor: accentBg }}
            aria-label={
              isCustomizable ? `Customise ${product.name}` : `Add ${product.name} to cart`
            }
            data-testid={TID.addToCartBtn(product.id)}
          >
            {isCustomizable ? (
              <Wand2 className="w-5 h-5 text-[var(--cw-text)]" />
            ) : (
              <Plus className="w-5 h-5 text-[var(--cw-text)]" />
            )}
          </button>
        </div>
      </div>

      {isCustomizable && (
        <CustomizeHamperDialog
          open={customOpen}
          onOpenChange={setCustomOpen}
          product={product}
        />
      )}
    </article>
  );
};
